import { StyleRoot } from "@ttc3k/trekker";
import { PostHogErrorBoundary, PostHogProvider } from "posthog-js/react";
import { type ReactNode, useEffect, useState } from "react";
import { ApolloProviderPublic } from "shared/apollo";
import { PoweredByWhereabouts } from "shared/components/atoms";
import { ErrorBoundaryPage } from "shared/components/root";
import { PostHogAppContext, usePostHog } from "shared/hooks/usePostHog";
import { Flex } from "styled-system/jsx";
import styles from "styled-system/styles.css?inline";

export type PublicPayerPageShellProps = {
  ctxHeaders: Record<string, string>;
  /** Passed to PostHog `init` to distinguish traffic sources. */
  posthogInstanceId: string;
  children: ReactNode;
};

/**
 * Shared chrome for public payer-facing pages (`/order/:id`, `/invoice/:number`):
 * public Apollo, PostHog, white column layout, powered-by footer.
 */
export function PublicPayerPageShell({
  ctxHeaders,
  posthogInstanceId,
  children,
}: PublicPayerPageShellProps) {
  const { posthog, initPostHog, registerPostHog, getPostHogErrorCaptureData } =
    usePostHog();

  initPostHog(posthogInstanceId);
  registerPostHog({
    appContext: PostHogAppContext.WhereaboutsNext,
  });

  const [isLoaded, setIsLoaded] = useState(false);
  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <ApolloProviderPublic ctxHeaders={ctxHeaders}>
      <PostHogProvider client={posthog}>
        <StyleRoot styles={[styles]}>
          <Flex
            flexDir="column"
            alignItems="center"
            gap="500"
            bgColor="white"
            py={{
              base: "300",
              md: "120px",
            }}
            px={{
              base: "200",
              md: "600",
            }}
            transition="all 0.2s ease-in-out"
          >
            <PostHogErrorBoundary
              fallback={<ErrorBoundaryPage showResetAndSignOut={false} />}
              additionalProperties={getPostHogErrorCaptureData({
                fileName: "PublicPayerPageShell.tsx",
                extra: {
                  errorBoundaryMessage:
                    "Public payer error boundary ... <ErrorBoundaryPage /> displayed",
                },
              })}
            >
              {children}
            </PostHogErrorBoundary>
            {isLoaded && (
              <PoweredByWhereabouts
                containerProps={{
                  css: { breakInside: "avoid", pageBreakInside: "avoid" },
                }}
              />
            )}
          </Flex>
        </StyleRoot>
      </PostHogProvider>
    </ApolloProviderPublic>
  );
}
